import { Component, inject, OnInit, signal } from '@angular/core';
import { CardComponent } from '../../../components/card/card.component';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { RangeFormComponent } from '../../../components/range-form/range-form.component';
import { BackendService } from '../../../api/bff/backend.service';
import { HttpClient } from '@angular/common/http';
import { GeoData } from '../../../types/GeoData';
import { MetricsGraphGroupComponent } from './metrics-graph-group/metrics-graph-group.component';
import { BreadcrumbService } from '../../../state/breadcrumb/breadcrumb.service';
import { TelemetryGroup, TelemetryGraph } from '@shared/interfaces/telemetry';
import { UIGraph } from './graphs/graph.interface';
import { createHeatmap } from './graphs/heatmap';
import { createGroupedBar } from './graphs/groupedBar';
import { createBar } from './graphs/bar';
import { createLine } from './graphs/line';
import { createGeo } from './graphs/geo';
import dayjs, { DateRange } from '../../../utils/dayjs/dayjs';
import { MetricFilter } from '../../../types/Metrics';
import { EnumerationFilterComponent } from './enumeration-filter/enumeration-filter.component';

@Component({
  selector: 'app-service-metrics-page',
  imports: [CardComponent, RangeFormComponent, MetricsGraphGroupComponent, EnumerationFilterComponent],
  templateUrl: './service-metrics-page.component.html',
  styleUrl: './service-metrics-page.component.css',
})
export class ServiceMetricsPageComponent implements OnInit {
  private backend = inject(BackendService);
  private httpClient = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private breadcrumbs = inject(BreadcrumbService);
  private world!: GeoData;
  protected title = signal('');
  protected graphs = signal<UIGraph[] | null>([]);
  protected ranges = [
    { value: 7, label: $localize`7 days` },
    { value: 30, label: $localize`30 days` },
    { value: 365, label: $localize`12 months` },
  ];
  protected filters = signal<MetricFilter[]>([]);

  public async ngOnInit() {
    this.world = await firstValueFrom(this.httpClient.get<GeoData>('world-atlas/countries-110m.json'));
    await this.fetchFilters();
    this.fetchData();
  }

  protected onRangeChange(range: DateRange) {
    const filters = this.filters();
    const filter = filters.find((el) => el.type === 'range');
    if (!filter) return;
    filter.selected.max = range.end.format('YYYY-MM-DD');
    filter.selected.min = range.start.format('YYYY-MM-DD');
    this.filters.set([...filters]);

    this.fetchData();
  }

  protected onEnumerationChange(selected: string) {
    const filters = this.filters();
    const filter = filters.find((el) => el.type === 'enumeration');
    if (!filter) return;
    filter.selected = selected;
    this.filters.set([...filters]);

    this.fetchData();
  }

  private createGraphs(graphs: TelemetryGraph[]): UIGraph[] {
    return graphs
      .map((graph): UIGraph | null => {
        switch (graph.type) {
          case 'group':
            return this.createGraphGroup(graph);
          case 'blocks':
            return graph;
          case 'imshow':
            return createHeatmap(graph);
          case 'bar_grouped':
            return createGroupedBar(graph);
          case 'bar':
            return createBar(graph);
          case 'line':
            return createLine(graph);
          case 'scatter_geo':
            return createGeo(graph, this.world);
          default:
            console.error(`Unknown graph type: "${(graph as unknown as { type: string }).type}"`);
            return null;
        }
      })
      .filter((graph) => graph !== null);
  }

  private createGraphGroup(graph: TelemetryGroup) {
    const type = 'group' as const;
    const title = graph.title;
    const variant = graph.variant;
    const graphs = this.createGraphs(graph.graphs);

    return { type, title, graphs, variant };
  }

  private async fetchFilters() {
    const serviceId = this.route.snapshot.paramMap.get('service') || '';
    const data = await firstValueFrom(this.backend.getTelemetryFilters(serviceId));
    const filters: MetricFilter[] = [];
    for (const [name, filter] of Object.entries(data)) {
      if (filter.type === 'range') {
        filters.push({
          name,
          type: filter.type,
          values: {
            min: filter.min,
            max: filter.max,
          },
          selected: {
            min: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
            max: dayjs().format('YYYY-MM-DD'),
          },
        });
      }
      if (filter.type === 'enumeration') {
        filters.push({
          name,
          type: filter.type,
          values: ['All', ...filter.values],
          selected: 'All',
        });
      }
    }
    this.filters.set(filters);
  }

  private fetchData() {
    const serviceId = this.route.snapshot.paramMap.get('service') || '';
    this.graphs.set([]);
    firstValueFrom(this.backend.getTelemetry(serviceId, this.filters())).then((data) => {
      this.title.set(data.name);
      this.breadcrumbs.setLabel(serviceId, data.name);
      this.graphs.set(data.total_rows === 0 ? null : this.createGraphs(data.graphs));
    });
  }
}
